import { useState, useEffect } from "react";
import { countries, languages } from "../../global/countriesAndSources";

export default function SearchComponent({searchTerm, setSearchTerm, handleQuery, dropdownList, otherButton}) {
    
    // États pour la recherche dans les dropdowns
    const [dropdownSearch, setDropdownSearch] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);

    // Initialiser le champ de recherche dropdown avec la valeur sélectionnée
    useEffect(() => {
        if (dropdownList) {
            // Si on est sur une valeur par défaut, laisser le champ vide
            const isDefault = (dropdownList.select === 'Pays' && dropdownList.value === 'ac') ||
                             (dropdownList.select === 'AllLanguages' && dropdownList.value === 'al') ||
                             (dropdownList.select === 'CountryLanguages' && dropdownList.listLang && 
                              dropdownList.listLang.length > 0 && dropdownList.value === dropdownList.listLang[0].language_code);
            
            if (isDefault) {
                setDropdownSearch('');
            } else {
                setDropdownSearch(getDisplayValue(dropdownList.value));
            }
        }
    }, [dropdownList?.value, dropdownList?.select, dropdownList?.listLang]);

    // Filtrer les éléments selon le type de dropdown
    useEffect(() => {
        if (!dropdownList) {
            setFilteredItems([]);
            return;
        }

        let items = [];
        
        if (dropdownList.select === 'Pays') {
            items = countries;
        } else if (dropdownList.select === 'CountryLanguages') {
            items = dropdownList.listLang || [];
        } else if (dropdownList.select === 'AllLanguages') {
            items = languages;
        }

        if (dropdownSearch.trim() === '') {
            setFilteredItems(items);
        } else {
            const filtered = items.filter(item => {
                if (dropdownList.select === 'Pays') {
                    return item.name.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
                           item.code.toLowerCase().includes(dropdownSearch.toLowerCase());
                } else {
                    return item.language_name.toLowerCase().includes(dropdownSearch.toLowerCase()) ||
                           item.language_code.toLowerCase().includes(dropdownSearch.toLowerCase());
                }
            });
            setFilteredItems(filtered);
        }
    }, [dropdownSearch, dropdownList]);

    // Obtenir le placeholder dynamique selon la sélection
    const getDynamicPlaceholder = () => {
        if (!dropdownList) return "Rechercher...";
        
        const currentSelection = getDisplayValue(dropdownList.value);
        const isDefault = (dropdownList.select === 'Pays' && dropdownList.value === 'ac') ||
                         (dropdownList.select === 'AllLanguages' && dropdownList.value === 'al') ||
                         (dropdownList.select === 'CountryLanguages' && dropdownList.listLang && 
                          dropdownList.listLang.length > 0 && dropdownList.value === dropdownList.listLang[0].language_code);
        
        if (isDefault) {
            // Si on est sur une valeur par défaut, afficher le placeholder de recherche
            if (dropdownList.select === 'Pays') {
                return "Tous les pays - Rechercher un pays...";
            } else if (dropdownList.select === 'AllLanguages') {
                return "Toutes les langues - Rechercher une langue...";
            } else if (dropdownList.select === 'CountryLanguages') {
                const defaultLang = dropdownList.listLang[0];
                return `${defaultLang.language_name} - Rechercher une langue...`;
            }
        } else {
            // Si on a une sélection spécifique, l'afficher dans le placeholder
            return `${currentSelection} - Rechercher une autre...`;
        }
        
        return "Rechercher...";
    };
    const getDisplayValue = (value) => {
        if (!dropdownList || !value) return '';
        
        if (dropdownList.select === 'Pays') {
            if (value === 'ac') return 'Tous les pays';
            const country = countries.find(c => c.code === value);
            return country ? country.name : '';
        } else if (dropdownList.select === 'CountryLanguages') {
            const lang = dropdownList.listLang?.find(l => l.language_code === value);
            return lang ? lang.language_name : '';
        } else if (dropdownList.select === 'AllLanguages') {
            if (value === 'al') return 'Toutes les langues';
            const lang = languages.find(l => l.language_code === value);
            return lang ? lang.language_name : '';
        }
        return '';
    };

    const handleDropdownSearchChange = (e) => {
        setDropdownSearch(e.target.value);
        setIsDropdownOpen(true);
    };

    const handleItemSelect = (value, displayName) => {
        // Créer un événement simulé pour maintenir la compatibilité
        const syntheticEvent = {
            target: {
                value: value,
                name: 'country'
            }
        };
        dropdownList.handle(syntheticEvent);
        setDropdownSearch(displayName);
        setIsDropdownOpen(false);
    };

    const handleDropdownInputFocus = () => {
        setIsDropdownOpen(true);
    };

    const handleDropdownInputBlur = () => {
        setTimeout(() => {
            setIsDropdownOpen(false);
        }, 200);
    };

    // Vérifier si on est sur une valeur par défaut
    const isDefaultValue = () => {
        if (!dropdownList) return false;
        
        if (dropdownList.select === 'Pays') {
            return dropdownList.value === 'ac';
        } else if (dropdownList.select === 'AllLanguages') {
            return dropdownList.value === 'al';
        } else if (dropdownList.select === 'CountryLanguages' && dropdownList.listLang && dropdownList.listLang.length > 0) {
            return dropdownList.value === dropdownList.listLang[0].language_code;
        }
        return false;
    };

    const clearDropdownSearch = () => {
        // Remettre les valeurs par défaut selon le type
        if (dropdownList.select === 'Pays') {
            handleItemSelect('ac', 'Tous les pays');
        } else if (dropdownList.select === 'AllLanguages') {
            handleItemSelect('al', 'Toutes les langues');
        } else if (dropdownList.select === 'CountryLanguages' && dropdownList.listLang && dropdownList.listLang.length > 0) {
            // Prendre la première langue de la liste comme langue par défaut
            const defaultLang = dropdownList.listLang[0];
            handleItemSelect(defaultLang.language_code, defaultLang.language_name);
        }
        
        // Laisser le champ vide (puisque les valeurs par défaut = champ vide)
        setDropdownSearch('');
        setFilteredItems(dropdownList.select === 'Pays' ? countries : 
                        dropdownList.select === 'CountryLanguages' ? (dropdownList.listLang || []) : 
                        languages);
        setIsDropdownOpen(false); // Fermer la dropdown puisqu'on revient au paramètre de base
    };

    const renderDropdownItems = () => {
        if (!dropdownList) return null;

        const items = [];

        // Ajouter directement les éléments filtrés (sans les options "Tous")
        filteredItems.forEach((item) => {
            if (dropdownList.select === 'Pays') {
                items.push(
                    <div
                        key={item.code}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleItemSelect(item.code, item.name)}
                    >
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-500">{item.code}</div>
                    </div>
                );
            } else {
                items.push(
                    <div
                        key={item.language_code}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleItemSelect(item.language_code, item.language_name)}
                    >
                        <div className="font-medium">{item.language_name}</div>
                        <div className="text-sm text-gray-500">{item.language_code}</div>
                    </div>
                );
            }
        });

        if (items.length === 0) {
            items.push(
                <div key="no-results" className="px-3 py-2 text-gray-500 text-center">
                    Aucun résultat trouvé
                </div>
            );
        }

        return items;
    };

    return (
        <form onSubmit={handleQuery} className="flex flex-col lg:flex-row items-center mb-4">
            <div className="w-full lg:flex-grow lg:mr-2">
                <input
                    type="text"
                    placeholder="Rechercher..."
                    className="w-full px-3 py-2 mb-2 lg:mb-0 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>
            
            {/* Dropdown avec recherche */}
            {dropdownList && (
                <div className="relative w-full lg:w-auto mb-2 lg:mb-0 lg:mr-2">
                    <input
                        type="text"
                        placeholder={getDynamicPlaceholder()}
                        className="w-full lg:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-48"
                        value={dropdownSearch}
                        onChange={handleDropdownSearchChange}
                        onFocus={handleDropdownInputFocus}
                        onBlur={handleDropdownInputBlur}
                    />
                    
                    {dropdownSearch && (
                        <button
                            type="button"
                            onClick={clearDropdownSearch}
                            className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                    )}
                    
                    {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            {renderDropdownItems()}
                        </div>
                    )}
                    
                    {/* Champ caché pour maintenir la compatibilité */}
                    <input
                        type="hidden"
                        name="country"
                        value={dropdownList.value}
                    />
                </div>
            )}
            
            <button
                type="submit"
                className="w-full lg:w-auto mb-2 lg:mb-0 bg-green-500 hover:bg-green-700 text-white font-bold lg:mr-2 py-2 lg:px-4 rounded-lg whitespace-nowrap"
            >
                Recherche
            </button>
            {
                otherButton && 
                    <button
                        type="button"
                        className="w-full lg:w-auto bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg whitespace-nowrap"
                        onClick={otherButton.handle}
                    >
                        {otherButton.title}
                    </button>
            }
        </form>
    )
}